import React, { useState } from "react";
import { Card, Form } from "react-bootstrap";
import { onSuccess, useApiMutation } from "../utils/api";
import { Trash2 } from "lucide-react";
import { EditModal } from "./EditIdeaModal";

type VoteType = "like" | "meh" | "dislike" | "approve";

interface IdeaCardProps {
  idea: {
    iduuid: string;
    title: string;
    image: string;
    cost: number;
    link: string;
    votes_summary: {
      self: VoteType | null;
      like: string[];
      meh: string[];
      dislike: string[];
      approve: string[];
    };
    comments: {
      uuid: string;
      comment: string;
      user: { name: string };
      created_at: Date;
    }[];
  };
  refetch: () => void;
}

const formatCost = (cost: number) => {
  if (cost.toString().match(/^\d+(\.\d{2})?$/)) {
    return `$${parseFloat(cost.toString()).toLocaleString()}`;
  } else {
    return cost;
  }
};

export const IdeaCard = ({ idea, board, refetch }: IdeaCardProps) => {
  const { post } = useApiMutation();
  const [newComment, setNewComment] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [objectFitAlt, setObjectFitAlt] = useState(false);

  const handleVote = (vote_type: VoteType) => {
    post(`/api/user/vote/${idea.id}`, { vote_type }).then(onSuccess(refetch));
  };

  const addComment = (e: React.FormEvent) => {
    e.preventDefault();

    post(`/api/user/add_comment/${idea.id}`, { comment: newComment }).then(
      onSuccess(() => {
        refetch();
        setNewComment("");
      })
    );

    setNewComment("");
  };

  const deleteComment = (commentId: number) => {
    if (confirm("Delete this comment?")) {
      post(`/api/user/delete_comment/${commentId}`).then(onSuccess(() => refetch()));
    }
  };

  const formatDate = (date?: Date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  return (
    <Card className="shadow border-0">
      {idea.image && (
        <div className="position-relative hover-show-parent">
          <Card.Img
            variant="top"
            src={idea.image}
            loading="lazy"
            style={{
              height: 150,
              objectFit: objectFitAlt ? "contain" : "cover",
            }}
          />
          <div className="hover-show-child position-absolute top-5px right-5px">
            <a
              href={idea.link}
              target="_blank"
              rel="noopener noreferrer"
              className="idea-action-button soft-black"
            >
              <i className="fas fa-external-link-alt" />
            </a>
            <div
              className="idea-action-button mt-1"
              onClick={() => setObjectFitAlt(!objectFitAlt)}
            >
              <i className="fas fa-search" />
            </div>
          </div>
        </div>
      )}
      <Card.Body className="p-2">
        <div className="d-flex justify-content-between align-items-start">
          <a
            className="fs-17 black no-underline"
            href={idea.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {idea.title}
          </a>
        </div>
        {idea.cost != null && (
          <div className="bg-light-gray d-inline-block rounded p-1 mt-1">
            {formatCost(idea.cost)}
          </div>
        )}

        <div className="d-flex gap-3 justify-content-around mt-2">
          {[
            ["like", "far fa-grin", "fas blue", "gray"],
            ["meh", "far fa-meh", "fas blue", "gray"],
            ["dislike", "far fa-frown", "fas blue", "gray"],
            ["approve", "fas fa-check", "green", "gray-a"],
          ].map(([type, icon, active, inactive]) => {
            const isActive = idea.votes_summary.self === type;
            return (
              <button
                key={type}
                className="btn-text flex-1"
                onClick={() => handleVote(type as "like" | "meh" | "dislike" | "approve")}
              >
                <i className={`${icon} ${isActive ? active : inactive}`} />
                <span className="fs-12 ml-1 gray">
                  {idea.votes_summary[type as "like" | "meh" | "dislike" | "approve"]
                    .length || ""}
                </span>
              </button>
            );
          })}
          <button className="btn-text flex-1" onClick={() => setShowEditModal(true)}>
            <i className="far fa-edit" />
          </button>
        </div>

        <div className="mt-2">
          {idea.comments.map((comment) => (
            <div key={comment.id} className="mb-2 p-2 bg-light rounded fs-14">
              <div className="fs-12">
                <span className="fw-bold">{comment.user.name}:</span>
                <span className="fs-14 ml-1">{comment.comment}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center fs-12 text-muted mt-1">
                <span>{formatDate(comment.created_at)}</span>
                <button
                  className="btn-text fs-10"
                  onClick={() => deleteComment(comment.id)}
                >
                  <Trash2 className="gray" size={10} />
                </button>
              </div>
            </div>
          ))}
          <Form onSubmit={addComment} className="mt-3">
            <input
              type="text"
              placeholder="Add comment"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="subtle-input w-100"
            />
          </Form>
        </div>
      </Card.Body>

      {showEditModal && (
        <EditModal
          idea={idea}
          board={board}
          close={() => setShowEditModal(false)}
          refetch={refetch}
        />
      )}
    </Card>
  );
};
